import {
  DataVoyagerWidgetData,
  ReportWidgetData,
  TableWidgetData,
  WidgetId,
  WidgetType,
} from '@/models/PanelWidgets';
import { openWidget, OpenWidgetPayload } from '@/store/reducers/UIState';
import { ReportWidgetDataFromApi, reportWidgetFactory } from '@/models/Report';
import { ThreadId } from '@/models/Thread';

import { BoundingBox } from '@allenai/pdf-components/src/components/types/boundingBox'; // eslint-disable-line import/no-unresolved
import { PayloadAction } from '@reduxjs/toolkit';
import invariant from 'invariant';

const EmptyReport: ReportWidgetDataFromApi = {
  id: 'TODO',
  sections: [
    {
      id: 'INIT',
      title: 'Loading...',
      text: 'Loading latest report',
      citations: [],
    },
  ],
};

export const OPEN_WIDGET_TYPE = 'UIState/openWidget';

export const getWidgetTypefromId = (widgetId: string): WidgetType => {
  const widgetType = widgetId.split('-')[0];
  invariant(widgetType in WidgetType, `Invalid widget id: ${widgetId}`);
  return widgetId.split('-')[0] as WidgetType;
};

export const createReaderWidgetId = (corpusId: number): string => {
  return `${WidgetType.READER}-${corpusId}`;
};

export const createTableWidgetId = (threadId: ThreadId): string => {
  return `${WidgetType.TABLE}-${threadId}`;
};

export const createPaperFinderWidgetId = (paperFinderId: string): string => {
  return `${WidgetType.PAPER_FINDER}-${paperFinderId}`;
};

export const createReportWidgetId = (reportId: string): string => {
  return `${WidgetType.REPORT}-${reportId}`;
};

export const createDataVoyagerWidgetId = (dataVoyagerId: string): string => {
  return `${WidgetType.DATA_VOYAGER}-${dataVoyagerId}`;
};

export const openReaderWidget = ({
  currentThreadId,
  corpusId,
  highlights,
}: {
  currentThreadId: ThreadId;
  corpusId: number;
  highlights?: BoundingBox[];
}): PayloadAction<OpenWidgetPayload, typeof OPEN_WIDGET_TYPE> => {
  return openWidget({
    threadId: currentThreadId,
    widgetId: createReaderWidgetId(corpusId),
    widgetData: {
      type: WidgetType.READER,
      corpusId,
      ...(highlights ? { highlights } : {}),
    },
  });
};

export const openTableWidget = ({
  currentThreadId,
  tableData,
}: {
  currentThreadId: ThreadId;
  tableData?: TableWidgetData;
}): PayloadAction<OpenWidgetPayload, typeof OPEN_WIDGET_TYPE> => {
  return openWidget({
    threadId: currentThreadId,
    widgetId: createTableWidgetId(currentThreadId),
    widgetData: tableData ?? null,
  });
};

export const openPaperFinderWidget = ({
  threadId,
  widgetId,
}: {
  threadId: ThreadId;
  widgetId: WidgetId;
}): PayloadAction<OpenWidgetPayload, typeof OPEN_WIDGET_TYPE> => {
  return openWidget({
    threadId,
    widgetId: createPaperFinderWidgetId(widgetId),
    widgetData: null,
  });
};

export const openReportWidget = ({
  currentThreadId,
  reportData,
}: {
  currentThreadId: ThreadId;
  reportData?: ReportWidgetData | null;
}): PayloadAction<OpenWidgetPayload, typeof OPEN_WIDGET_TYPE> => {
  return openWidget({
    threadId: currentThreadId,
    widgetId: createReportWidgetId(reportData?.id ?? 'TODO'),
    widgetData: reportData ?? reportWidgetFactory(EmptyReport),
  });
};

export const openDataVoyagerWidget = ({
  currentThreadId,
  dvData,
}: {
  currentThreadId: ThreadId;
  dvData?: DataVoyagerWidgetData | null;
}): PayloadAction<OpenWidgetPayload, typeof OPEN_WIDGET_TYPE> => {
  return openWidget({
    threadId: currentThreadId,
    widgetId: createDataVoyagerWidgetId(dvData?.id ?? 'TODO'),
    widgetData: dvData ?? null,
  });
};
